import arrayMove from 'array-move';
import { get, omit } from 'lodash';
import type { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil, toArray, withLatestFrom } from 'rxjs/operators';
import Cookies from 'universal-cookie';

import { ConnectionType, connect } from '@swf/core/api/connect';
import { subscribe as apiSubscribe } from '@swf/core/api/subscribe';
import { AccValEventType, CRMZone, FeatureFlag, Site, SuspendAccountCode } from '@swf/core/enums';
import { ErrorCode } from '@swf/core/enums/errorCode';
import { StatusCode, StatusSubcode } from '@swf/core/enums/statusCode';
import type { RequestError } from '@swf/core/errors/RequestError';
import type { Modal } from '@swf/core/models/modal';
import type { SessionResponse } from '@swf/core/models/session';
import { LoginSource } from '@swf/core/models/session';
import type { Dispatch, EffectParams, RootState } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import {
  BanPopupType,
  License,
  LugasPlayerStatus,
  PlayerValPopupType
} from '@swf/core/types/Account';
import CookieNames from '@swf/core/types/Cookies';
import type {
  ForceLogoutNotification,
  LobbyNotification,
  NotificationMessage
} from '@swf/core/types/Notification';
import { NotificationCode } from '@swf/core/types/Notification';
import type { BoundRequestOptions, ResponseHeaders } from '@swf/core/types/Request';
import { customEvent, timeout, velocityControl } from '@swf/core/utils';
import { tryFetch } from '@swf/core/utils/api';

import type { AuthError } from '.';

interface ModalInfo {
  path: string;
  locationState?: object;
  shouldBeVisible: (
    rootState: RootState,
    license: License,
    loginSource: LoginSource | undefined,
    temporaryPassword: string | undefined
  ) => boolean;
}

const ONE_DAY_IN_MINUTES = 24 * 60;
const ONE_WEEK_IN_MINUTES = 24 * 60 * 7;
const SECOND = 1000;
const subscriptions: Subscription[] = [];
const cookies = new Cookies();
let pending = false;

const delayedNotifications = [
  'notification.login.event',
  'notification.session.summary',
  'notification.lobby.msg',
  'notification.geocomply.locate',
  'notification.admin.info',
  'notification.player.blacklisted'
];

const sub = (
  args: Parameters<typeof apiSubscribe>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listener: (value: any) => void
) => subscriptions.push(apiSubscribe(...args).subscribe(listener));

const logoutNotificationFilter = (message: NotificationMessage) =>
  ['session.logout.reply', 'notification.tokenExpireWarning'].includes(message.headers.type);

const forceLogoutNotificationFilter = (message: NotificationMessage) =>
  'notification.session.forcelogout' === message.headers.type;

const lobbyLogoutMessageNotificationFilter = (message: NotificationMessage) => {
  const {
    headers: { type },
    body: { notification }
  } = message;
  const { notificationCode } = notification as LobbyNotification;

  return type === 'notification.lobby.msg' && notificationCode === NotificationCode.Error;
};

const notificationComparator = (msgA: NotificationMessage, msgB: NotificationMessage) =>
  delayedNotifications.indexOf(msgA.headers.type) - delayedNotifications.indexOf(msgB.headers.type);

export const subscribe = async (dispatch: Dispatch, loginObservable: Subject<boolean>) => {
  if (subscriptions.length) {
    return;
  }

  dispatch.notification.reset();
  const rootState = getState();
  const notificationPath = select.site.getNotificationSubscriptionPath(rootState);
  let loginSuccess = false;

  if (!select.feature.check(rootState, FeatureFlag.LoyaltyWidgetRefresh)) {
    const { token } = select.session.getLegacySession(rootState);
    const connection = connect(ConnectionType.Rewards);
    sub([`/topic/widget/${token}`, {}, connection], ({ body }) =>
      dispatch.rewardsLegacy.enqueue(body)
    );
  } else {
    dispatch.rewards.fetchRewards();
  }

  subscriptions.push(
    apiSubscribe<NotificationMessage>(notificationPath)
      .pipe(
        filter((message) =>
          forceLogoutNotificationFilter(message)
            ? false
            : loginSuccess || !delayedNotifications.includes(message.headers.type)
        )
      )
      .subscribe(dispatch.notification.push)
  );

  apiSubscribe<NotificationMessage>(notificationPath)
    .pipe(
      filter(({ headers: { type } }) => delayedNotifications.includes(type)),
      takeUntil(loginObservable),
      toArray(),
      map((notificationMessages) => {
        notificationMessages.sort(notificationComparator);
        return notificationMessages;
      })
    )
    .subscribe((delayedMessages) => {
      loginSuccess = true;
      delayedMessages.forEach((notification) => dispatch.notification.push(notification));
    });

  const lobbyNotification = apiSubscribe<NotificationMessage>(notificationPath).pipe(
    startWith(undefined),
    filter<NotificationMessage | undefined>(
      (message) => !message || lobbyLogoutMessageNotificationFilter(message)
    ),
    take(2)
  );

  subscriptions.push(
    apiSubscribe<NotificationMessage>('/user/queue/messages')
      .pipe<NotificationMessage, [NotificationMessage, NotificationMessage | undefined]>(
        filter(logoutNotificationFilter),
        withLatestFrom(lobbyNotification)
      )
      .subscribe(async ([logoutMessage, lobbyMessage]) => {
        await dispatch.notification.push(logoutMessage);
        if (lobbyMessage) {
          dispatch.notification.push(lobbyMessage);
        }
      })
  );

  subscriptions.push(
    apiSubscribe<NotificationMessage>(notificationPath)
      .pipe<NotificationMessage, [NotificationMessage, NotificationMessage | undefined]>(
        filter(forceLogoutNotificationFilter),
        withLatestFrom(lobbyNotification)
      )
      .subscribe(([logoutMessage, lobbyMessage]) => {
        const logoutNotification = logoutMessage.body.notification as ForceLogoutNotification;
        const { msg: logoutBodyMessage } = logoutNotification;

        if (!logoutBodyMessage && lobbyMessage) {
          const { msg: lobbyBodyMessage } = lobbyMessage.body.notification as LobbyNotification;
          logoutNotification.msg = lobbyBodyMessage;
          logoutMessage.body.notification = logoutNotification;
        }
        dispatch.notification.push(logoutMessage);
      })
  );
};

export const unsubscribe = () =>
  subscriptions
    .splice(0, subscriptions.length)
    .forEach((subscription) => subscription.unsubscribe());

const showBanModal = (rootState: RootState, loginSource: LoginSource | undefined) =>
  select.account.getBanPopup(rootState) !== BanPopupType.NotBanned &&
  loginSource === LoginSource.Login &&
  select.account.isRealMoneyOK(rootState);

const loginModals: ModalInfo[] = [
  {
    path: '/notifications/cruks-blacklist',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.site.getId(rootState) === Site.NL &&
      loginSource === LoginSource.Login &&
      select.account.getPlayerValPopupType(rootState) === PlayerValPopupType.Blacklisted
  },
  {
    path: '/notifications/cz-blacklist',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.CZ &&
      loginSource === LoginSource.Login &&
      [
        PlayerValPopupType.Blacklisted,
        PlayerValPopupType.DocVerificationRequired,
        PlayerValPopupType.RmActivityRestricted
      ].includes(select.account.getPlayerValPopupType(rootState) as number)
  },
  {
    path: '/notifications/ee-population-register',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.EE &&
      loginSource === LoginSource.Login &&
      ([
        PlayerValPopupType.DocVerificationRequired,
        PlayerValPopupType.RmActivityRestricted
      ].includes(select.account.getPlayerValPopupType(rootState) as number) ||
        select.account.shouldPreventLogin(rootState))
  },
  {
    path: '/notifications/anj-national-exclusion',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.FR &&
      loginSource === LoginSource.Login &&
      select.account.getBanPopup(rootState) === BanPopupType.RemainBanned
  },
  {
    path: '/notifications/gamstop-exclusion',
    shouldBeVisible: (rootState) =>
      select.account.getWebClientDialog(rootState) === '/notifications/gamstop-exclusion'
  },
  {
    path: '/account/cashier/bankId',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.SE &&
      loginSource === LoginSource.Registration &&
      get(select.modal.getCurrent(rootState), ['locationState', 'bankId'])
  },
  {
    path: '/notifications/account-cancelled',
    shouldBeVisible: (rootState) => select.account.isAccountCancelled(rootState)
  },
  {
    path: '/auth/login/sms',
    shouldBeVisible: () =>
      parseInt(cookies.get(CookieNames.LIMITED_MODE), 10) === StatusCode.SMS_VALIDATION_ENABLED
  },
  {
    path: '/notifications/quick-account-creation',
    shouldBeVisible: (rootState) => select.account.isQuickAccountCreationRequired(rootState)
  },
  {
    path: '/account/migration',
    shouldBeVisible: (rootState) =>
      select.account.isMigrationRequired(rootState) &&
      !select.account.isQuickAccountCreationRequired(rootState)
  },
  {
    path: '/auth/login/eula',
    shouldBeVisible: (rootState) =>
      !select.account.isMigrationRequired(rootState) && select.account.isEulaRequired(rootState)
  },
  {
    path: '/notifications/reset-password',
    shouldBeVisible: (rootState, license, loginSource, temporaryPassword) => !!temporaryPassword
  },
  {
    path: '/account/cashier/rmf',
    shouldBeVisible: (rootState, _, loginSource) =>
      !select.account.isMigrationRequired(rootState) &&
      select.account.shouldOpenRmf(rootState, loginSource)
  },
  {
    path: '/notifications/forced-occupation',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.CAON &&
      loginSource === LoginSource.Login &&
      select.account.getWebClientDialog(rootState) === '/notifications/forced-occupation'
  },
  {
    path: '/notifications/occupation-attestation',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      (select.account.getWebClientDialog(rootState) === '/notifications/occupation-attestation' ||
        select.account.getWebClientDialog(rootState) === '/notifications/merged-occupation')
  },
  {
    path: '/security-investigation',
    shouldBeVisible: (rootState) =>
      select.account.isSuspendRM(rootState) &&
      (!select.site.showVegasContent(rootState) ||
        (!select.account.isAgeVerified(rootState) && !select.account.isTemporaryAccount(rootState)))
  },
  {
    path: '/auth/force-change-password',
    shouldBeVisible: (rootState, license) =>
      license === License.ES &&
      select.account.isRealMoneyOK(rootState) &&
      select.account.isPasswordWeak(rootState)
  },
  {
    path: '/notifications/over25-first-time',
    shouldBeVisible: (rootState, license) =>
      license === License.UK && select.account.showUkaYoungUserDialog(rootState)
  },
  {
    path: '/notifications/reinstate-account-initial',
    shouldBeVisible: (rootState, license) =>
      license === License.EU && select.account.showReinstateDialog(rootState)
  },
  {
    path: '/notifications/login-cash-out',
    shouldBeVisible: (rootState) => select.account.getForcedCashoutEnabled(rootState)
  },
  {
    path: '/notifications/documents-verification',
    shouldBeVisible: (rootState) => select.account.requireDocumentValidation(rootState)
  },
  {
    path: '/notifications/banned-by-regulator',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.SE && showBanModal(rootState, loginSource)
  },
  {
    path: '/notifications/oasis-national-exclusion',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.site.showVegasContent(rootState) && showBanModal(rootState, loginSource)
  },
  {
    path: '/notifications/prevent-login-vegas',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.site.showVegasContent(rootState) &&
      select.account.shouldPreventLogin(rootState) &&
      loginSource === LoginSource.Login &&
      select.account.isRealMoneyOK(rootState)
  },
  {
    path: '/notifications/lifetime-limit-acknowledgement',
    shouldBeVisible: (rootState, license) =>
      license === License.USNJ && select.account.checkLifetimeLimitAck(rootState)
  },
  {
    path: '/notifications/verify-withdrawal-method',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.account.isVerifyWithdrawalMethod(rootState) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/poker-time-limit',
    shouldBeVisible: (rootState) =>
      select.account.getWebClientDialog(rootState) === '/notifications/poker-time-limit'
  },
  {
    path: '/notifications/login-information',
    shouldBeVisible: (_, license, loginSource) =>
      [
        License.ES,
        License.SE,
        License.IT,
        License.DK,
        License.PT,
        License.BG,
        License.USNJ,
        License.USPA,
        License.GR,
        License.CAON
      ].includes(license) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/validate-account-reminder',
    shouldBeVisible: (rootState, license, loginSource) =>
      loginSource === LoginSource.Login &&
      ((license === License.GR && select.account.tempAccount(rootState)) ||
        (license === License.FR &&
          [
            AccValEventType.WaitDocs,
            AccValEventType.ClosedByDocs,
            AccValEventType.SuspendedByDocs,
            AccValEventType.DocsMismatch,
            AccValEventType.DocsMismatchCorrected
          ].includes(select.account.getAccValEventType(rootState) as AccValEventType)))
  },
  {
    path: '/notifications/playing-limits-reminder',
    shouldBeVisible: (rootState, license, loginSource) =>
      loginSource === LoginSource.Login &&
      ((license === License.NL &&
        !(
          select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
          select.account.selfExclusionExpired(rootState)
        )) ||
        license === License.CZ)
  },
  {
    path: '/notifications/login-limits',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      (!!select.account.getDepositConfirmLimits(rootState) ||
        !!select.account.getGameConfirmLimits(rootState))
  },
  {
    path: '/auth/docverify',
    shouldBeVisible: (rootState, license, loginSource) => {
      const currentModal = select.modal.getCurrent(rootState);

      return (
        // tmp solution to solve routing conflicts when dealing with embedded account routes
        currentModal?.path !== '/account/details/verify-account/' &&
        !select.account.getAccValEventType(rootState) &&
        ((select.feature.check(rootState, FeatureFlag.AMLLoginMsg) &&
          !!select.account.getAmlEventType(rootState) &&
          loginSource === LoginSource.Login &&
          select.site.showVegasContent(rootState)) ||
          select.account.isKYCRequired(rootState) ||
          (!select.account.isAgeVerified(rootState) && [License.FR, License.IT].includes(license)))
      );
    }
  },
  {
    path: 'notifications/verify-account',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.feature.check(rootState, FeatureFlag.AMLLoginMsg) &&
      !!select.account.getAmlEventType(rootState) &&
      loginSource === LoginSource.Login &&
      !select.site.showVegasContent(rootState)
  },
  {
    path: '/notifications/login-account-information',
    shouldBeVisible: (rootState, license, loginSource) =>
      select.site.showVegasContent(rootState) &&
      loginSource === LoginSource.Login &&
      !!select.account.getAccountSummary(rootState)
  },
  {
    path: '/notifications/account-verified',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      select.feature.check(rootState, FeatureFlag.AMLLoginMsgVerified) &&
      select.account.amlVerified(rootState) &&
      select.account.amlShowVerifiedModal(rootState)
  },
  {
    path: '/notifications/pending-funds',
    shouldBeVisible: (rootState) =>
      !select.account.isAgeVerificationRequired(rootState) &&
      select.account.hasPendingFunds(rootState)
  },
  {
    path: '/notifications/casino-session-reminder',
    shouldBeVisible: (rootState) => select.account.isCasinoRealityCheckRequired(rootState)
  },
  {
    path: '/notifications/low-balance',
    shouldBeVisible: (rootState) =>
      select.feature.check(rootState, FeatureFlag.LowBalancePrompt) &&
      velocityControl(ONE_DAY_IN_MINUTES, select.localSettings.get(rootState).lowBalancePrompt)
  },
  {
    path: '/notifications/login-account-details',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.UK &&
      loginSource === LoginSource.Login &&
      select.account.accountDetailsRequired(rootState)
  },
  {
    path: '/notifications/self-excluded',
    shouldBeVisible: (rootState, license, loginSource) =>
      [License.USNJ, License.USPA, License.USMI].includes(license) &&
      loginSource === LoginSource.Login &&
      select.account.getInfoSelfExcludedMessage(rootState)
  },
  {
    path: '/notifications/value-proposition/before-you-begin',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.UK &&
      loginSource === LoginSource.Login &&
      select.account.displayValueProposition(rootState)
  },
  {
    path: '/notifications/active-self-exclusion',
    shouldBeVisible: (rootState, _, loginSource) =>
      select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
      loginSource === LoginSource.Login &&
      select.account.isSelfExcluded(rootState)
  },
  {
    path: '/notifications/expired-self-exclusion',
    shouldBeVisible: (rootState) =>
      select.feature.check(rootState, FeatureFlag.LoginSEDialogue) &&
      select.account.selfExclusionExpired(rootState)
  },
  {
    path: '/notifications/playing-insights',
    shouldBeVisible: (rootState, _, loginSource) =>
      loginSource === LoginSource.Login &&
      select.account.isRealMoneyOK(rootState) &&
      select.feature.check(rootState, FeatureFlag.VisibilityPlayingInsightsLoginPopup) &&
      velocityControl(ONE_WEEK_IN_MINUTES, select.localSettings.get(rootState).playingInsights)
  },
  {
    path: '/notifications/responsible-gaming-warning',
    shouldBeVisible: (_, license, loginSource) =>
      [License.EE, License.BE].includes(license) && loginSource === LoginSource.Login
  },
  {
    path: '/notifications/id-expiring',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.DE &&
      loginSource === LoginSource.Login &&
      select.account.getAccValEventType(rootState) === AccValEventType.WaitDocs
  },
  {
    path: '/notifications/id-expired',
    shouldBeVisible: (rootState, license, loginSource) =>
      license === License.DE &&
      loginSource === LoginSource.Login &&
      select.account.getAccValEventType(rootState) === AccValEventType.SuspendedByDocs
  }
];

export const getModalsByLicense = (modals: ModalInfo[], license: License) => {
  if ([License.ES, License.IT, License.SE].includes(license)) {
    const fromIndex = modals.findIndex((el) => el.path === '/account/cashier/rmf');
    const toIndex = modals.findIndex((el) => el.path === '/notifications/login-information');

    return arrayMove(modals, fromIndex, toIndex);
  }

  return modals;
};

export const verifyToken = async (
  token: string,
  signature: string,
  request: EffectParams['request'],
  hostname = ''
) => {
  const body = new URLSearchParams();
  body.append('token', token);
  body.append('signature', signature);

  const options: BoundRequestOptions = {
    httpDomain: hostname,
    service: '/wmtp/verifyToken',
    body,
    http: true,
    method: 'POST',
    retry: { count: 2, delay: (retryCount: number) => retryCount * SECOND }
  };

  await request(options);
};

export const fetchJWT = async (request: EffectParams['request']): Promise<string> => {
  const { body } = await request<{ token: string }>({
    service: '/api/v1-preview/auth/session',
    http: true
  });
  return body.token;
};

export const verifyOneTrustCookie = async (): Promise<void> => {
  const webId = cookies.get(CookieNames.WEB_ID);
  const { OneTrust } = window;
  const consentIntegrationData = OneTrust?.GetDomainData().ConsentIntegrationData;
  const CCID = consentIntegrationData?.consentPayload.identifier;
  if (!consentIntegrationData || (webId && webId === CCID)) {
    return;
  }

  OneTrust?.setDataSubjectId(webId);
  consentIntegrationData.consentPayload.isAnonymous = false;
  consentIntegrationData.consentPayload.identifier = webId;

  try {
    tryFetch(consentIntegrationData.consentApi, {
      method: 'POST',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(consentIntegrationData.consentPayload)
    });
    // eslint-disable-next-line no-empty
  } catch (ex) {}
};

export const verifyAccount = async (
  session: SessionResponse,
  { dispatch, request }: Pick<EffectParams, 'dispatch' | 'request'>,
  headers?: ResponseHeaders
) => {
  const state = getState();
  const oneTrustEnabled = select.feature.check(state, FeatureFlag.EnableOneTrust);
  const isEmbedded = select.site.isEmbedded(state);
  const JWTEnabled = select.feature.check(state, FeatureFlag.JWTEnabled);
  const { meta, headers: requestHeaders } = select.site.getRequestOptions(state);
  const payload = omit(session, 'userIntId');
  const { loginSource, signature } = payload;
  let { token } = payload;

  if (JWTEnabled) {
    if (loginSource === LoginSource.Registration || isEmbedded) {
      await verifyToken(token, signature, request);
      token = await fetchJWT(request);
    } else if (loginSource === LoginSource.VerifySession) {
      token = await fetchJWT(request);
    }
    requestHeaders.Authorization = `StarsWeb-Session ${token}`;
  } else {
    await verifyToken(token, signature, request);
    requestHeaders.token = token;
    requestHeaders.signature = signature;
  }

  if (!payload.account) {
    const { body } = await request({
      service: '/api/v1-preview/account',
      headers: requestHeaders,
      meta,
      retry: { count: 5, delay: (retryCount: number) => retryCount * SECOND }
    });
    payload.account = body;
  }

  if (oneTrustEnabled && [LoginSource.Login, LoginSource.Registration].includes(loginSource)) {
    verifyOneTrustCookie();
  }

  dispatch.session.response({ ...payload, token });
  if (payload.account?.oddsFormat) {
    cookies.set(CookieNames.ODDS_FORMAT, payload.account.oddsFormat, { path: '/' });
  }

  if (headers) {
    dispatch.auth.success(headers);
  }
};

export const sessionTransfer = async (
  options: BoundRequestOptions,
  { request }: Pick<EffectParams, 'request'>
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  try {
    return await request(options);
  } catch (error) {
    const { headers, body } = error as RequestError;
    if (
      headers?.statusCode === StatusCode.TRANSFER_APC_NOT_READY ||
      body?.code === ErrorCode.TRANSFER_APC_NOT_READY
    ) {
      await timeout(1000);
      return sessionTransfer(options, { request });
    }

    throw error;
  }
};

export async function checkLugasStatus(dispatch: Dispatch) {
  try {
    const { status } = await dispatch.account.getLugasStatus();
    if (status === LugasPlayerStatus.PENDING_LONG_SESSION) {
      dispatch.modal.unshift({ path: '/notifications/playing-timeout-break' });
    }
  } catch (e) {
    // do nothing
  }
}

export const loginSuccess = async (
  session: SessionResponse,
  { dispatch, request }: Pick<EffectParams, 'dispatch' | 'request'>,
  headers?: ResponseHeaders,
  temporaryPassword?: string
) => {
  if (pending) {
    return;
  }
  let rootState = getState();
  const payload = omit(session, 'userIntId');
  const { loginSource } = payload;
  const loginObservable = new Subject<boolean>();

  try {
    const location = select.modal.getCurrent(rootState);
    pending = true;
    await verifyAccount(session, { dispatch, request }, headers);

    try {
      await dispatch.feature.check();
      subscribe(dispatch, loginObservable);
      await dispatch.casino.fetchAppData();
    } catch (error) {
      subscribe(dispatch, loginObservable);
    }

    rootState = getState();
    const license = select.account.getLicense(rootState);

    const modals = getModalsByLicense(loginModals, license)
      .filter((modal) => modal.shouldBeVisible(rootState, license, loginSource, temporaryPassword))
      .map(({ path, locationState }) => ({
        path,
        locationState: { ...locationState, temporaryPassword }
      }));

    if (
      modals.length &&
      (!select.location.isEmbeddedRoute(rootState) || loginSource !== LoginSource.VerifySession)
    ) {
      if (!select.location.isEmbeddedRoute(rootState)) {
        dispatch.modal.close('/auth');
        dispatch.modal.unshift(modals);
      } else {
        dispatch.modal.replace(modals);
      }
    } else if ([LoginSource.Login, LoginSource.Registration].includes(session.loginSource)) {
      dispatch.modal.close('/auth');
    }

    const { restrictPlayingTime } = select.localSettings.get(rootState);
    if (restrictPlayingTime) {
      dispatch.modal.enqueue({
        path: '/notifications/restrict-playing-time',
        locationState: restrictPlayingTime
      });
    }

    const { sessionSummary } = select.localSettings.get(rootState);
    if (sessionSummary) {
      dispatch.modal.unique({
        path: '/notifications/session-summary-notification',
        locationState: sessionSummary
      });
    }

    if (!cookies.get(CookieNames.MY_NEWS_SEEN) && loginSource === LoginSource.Login) {
      await dispatch.crm.fetchZone({ zoneId: CRMZone.MY_NEWS });
      const { contents } = select.crm.getZone(getState(), CRMZone.MY_NEWS);
      if (contents?.length) {
        cookies.set(CookieNames.MY_NEWS_SEEN, true);
        dispatch.modal.enqueue({
          path: `/stars-crm/${CRMZone.MY_NEWS}`,
          locationState: { componentType: 'NavCarousel' }
        });
      }
    }
    if (loginSource === LoginSource.Login && get(location, ['locationState', 'path'])) {
      dispatch.modal.enqueue(get(location, 'locationState') as Modal);
    }
    if (license === License.DE) checkLugasStatus(dispatch);
  } finally {
    // eslint-disable-next-line require-atomic-updates
    pending = false;
    loginObservable.next(true);
    loginObservable.complete();
  }
};

export const loginError = async (
  payload: object,
  error: RequestError<AuthError>,
  dispatch: Dispatch
) => {
  const rootState = getState();
  const JWTEnabled = select.feature.check(rootState, FeatureFlag.JWTEnabled);
  const { statusCode, statusSubcode } = error.headers || {};
  const code = JWTEnabled ? error.body.code : statusCode;
  const site = select.site.getId(rootState);
  const isSiteUS = select.site.isSiteUS(rootState);
  const vegasContent = select.site.showVegasContent(rootState);
  const isSiteDK = [Site.DK].includes(site);
  const isSiteGR = site === Site.GR;

  // TODO remove with RAM1 integration
  customEvent({ tag: 'loginError', data: error });

  switch (code) {
    case isSiteDK &&
      statusSubcode === StatusSubcode.AUTH_SUB_AUTH_ERROR_ACCOUNT_CLOSED &&
      StatusCode.LOGIN_NOT_ALLOWED:
    case isSiteDK && ErrorCode.PERMANENTLY_EXCLUDED:
      dispatch.modal.replace([{ path: '/notifications/perm-banned' }]);
      break;
    case StatusCode.LOGIN_TIME_LIMIT_REACHED:
    case ErrorCode.TIME_LIMIT_REACHED:
      dispatch.modal.replace([
        {
          path: '/notifications/login-restrictions',
          locationState: { ...error.body }
        }
      ]);
      break;
    case StatusCode.LOGIN_TOTAL_LIMIT_REACHED:
    case ErrorCode.TOTAL_LIMIT_REACHED:
      dispatch.modal.replace([
        {
          path: '/notifications/login-total-restrictions',
          locationState: { ...error.body }
        }
      ]);
      break;
    case StatusCode.RSA_REQUIRED:
    case StatusCode.RSA_INVALID_CREDENTIALS:
    case ErrorCode.RSA_REQUIRED:
    case ErrorCode.RSA_INVALID_CREDENTIALS:
      dispatch.modal.replace([{ path: '/auth/login/rsa', locationState: payload }]);
      break;
    case StatusCode.PIN_REQUIRED:
    case StatusCode.TEMP_PIN_REQUIRED_SMS:
    case StatusCode.TEMP_PIN_REQUIRED:
    case ErrorCode.PIN_REQUIRED:
    case ErrorCode.TEMP_PIN_REQUIRED:
    case ErrorCode.TEMP_PIN_REQUIRED_SMS:
      dispatch.modal.replace([
        { path: '/auth/login/pin', locationState: { ...payload, errorCode: code } }
      ]);
      break;
    case StatusCode.LOGIN_EXPLICIT_LOGIN_DK_REQUIRED:
    case ErrorCode.NEMID_LOGIN_REQUIRED:
      dispatch.modal.replace([
        { path: '/auth/login/nemid', locationState: { ...payload, ...error.body } }
      ]);
      break;
    case statusSubcode === StatusSubcode.AUTH_SUB_ERROR_ACCOUNT_CLOSED_2 &&
      StatusCode.ACCOUNT_CLOSED:
    case ErrorCode.ACCOUNT_CLOSED:
      if (site === Site.DK) {
        dispatch.modal.replace([{ path: '/notifications/account-closed' }]);
      }
      if (site === Site.PT) {
        dispatch.modal.replace([
          {
            path: '/notifications/suspended-account',
            locationState: { code: SuspendAccountCode.Blacklisted }
          }
        ]);
      }

      dispatch.auth.error(error);
      break;
    case isSiteUS && StatusCode.BLOCKED_BANNED_BY_REGULATOR:
    case isSiteUS && ErrorCode.BANNED_BY_REGULATOR:
      dispatch.modal.replace([{ path: '/notifications/banned-by-regulator' }]);
      break;
    case [Site.UK, Site.CAON].includes(site) && StatusCode.LOGIN_SELF_EXCLUDED:
    case [Site.UK, Site.CAON].includes(site) && ErrorCode.SELF_EXCLUDED: {
      const selfExclusionEndDate = error.body.selfExclusionEndTime;
      if (new Date(Number(selfExclusionEndDate)) < new Date()) {
        dispatch.modal.replace([{ path: '/notifications/expired-self-excluded-popup' }]);
      } else {
        dispatch.modal.replace([
          {
            path: '/notifications/self-excluded-popup',
            locationState: { selfExclusionEndTime: selfExclusionEndDate }
          }
        ]);
      }
      break;
    }
    case isSiteGR && StatusCode.LONG_SESSION_COOL_OFF:
    case isSiteGR && ErrorCode.SESSION_COOL_OFF:
      dispatch.modal.replace([
        {
          path: '/notifications/session-cool-off',
          locationState: { longSessionCoolOff: error.body.longSessionCoolOff }
        }
      ]);
      break;
    case StatusCode.LOGIN_COOL_OFF:
    case ErrorCode.LOGIN_COOL_OFF:
      dispatch.modal.replace([
        {
          path: '/notifications/login-cool-off',
          locationState: { loginCoolOffLimit: error.body.loginCoolOffLimit }
        }
      ]);
      break;
    case StatusCode.PANIC_BUTTON_EXCLUDED:
    case ErrorCode.PANIC_BUTTON_EXCLUDED:
      if (site === Site.SE) {
        dispatch.modal.replace([
          {
            path: '/notifications/panic-button',
            locationState: { panicButonExclusionEndTime: error.body.panicButonExclusionEndTime }
          }
        ]);
      }
      if (vegasContent) {
        dispatch.modal.replace([{ path: '/notifications/cooling-off-period' }]);
      }
      break;
    case StatusCode.APC_ON_DIFF_DEVICE:
    case ErrorCode.APC_ON_DIFF_DEVICE:
      if (get(payload, 'rsaCode')) {
        dispatch.auth.error(error);
        break;
      }

      dispatch.modal.replace([{ path: '/auth/session-transfer', locationState: payload }]);
      break;
    case StatusCode.MITID_AUTH_REQUIRED:
    case ErrorCode.MITID_AUTH_REQUIRED:
      dispatch.modal.replace([
        { path: '/account/mitId', locationState: { path: '/auth/login/', ...payload } }
      ]);
      break;
    default:
      dispatch.auth.error(error);
  }

  return statusCode;
};
